import { MRT_ColumnDef } from "material-react-table";
import { JSX, MouseEvent, useState } from "react";
import { PuckPage } from "src/api/client-api/cms/useCreatePuckPage";
import { FoodsTextInput, FoodsTags, FoodsIcon } from "@foodpilot/foods";
import type { FoodsTagsProps } from "@foodpilot/foods";
import { GridLink } from "src/components/Grid/GridLink";
import { GridText } from "src/components/Grid/GridText";
import { Theme } from "@mui/material/styles";
import { Box, IconButton, MenuItem, Stack, TextField } from "@mui/material";
import { formatDateTime } from "src/utils/formatting";
import i18next, { TFunction } from "i18next";
import ContentCopy from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { CompanySector } from "src/api/client-api/companySectors/useSupplierSectors";
import { URLHandler } from "src/services/URLHandler";

export type BuildColumnsArgs = {
    t: TFunction;
    isServerSide: boolean;
    editingTitle: { slug: string; title: string } | null;
    isTitleUpdating: boolean;
    handleSaveTitle: () => void;
    setEditingTitle: (v: { slug: string; title: string } | null) => void;
    onEdit: (slug: string) => void;
    getEditUrl?: (slug: string) => string;
    pageType?: "CMS" | "PDF";
    scoreLabelById?: Record<string, string>;
    companyLabelById?: Record<string, string>;
    getPageRoles: (page: PuckPage) => string[];
    getRoleDisplayName: (code: string) => string;
    handleOpenRolesDialog: (page: PuckPage) => void;
    handleOpenScoreCompanyDialog?: (page: PuckPage) => void;
    handleOpenSettingsDialog?: (page: PuckPage) => void;
    onUpdateStatus?: (slug: string, status: "active" | "draft") => void;
    allSectors: CompanySector[];
    theme: Theme;
    navigate: (url: string) => void;
    canModifyPage: boolean;
};

const normalizeStringList = (v: unknown): string[] => {
    if (Array.isArray(v)) {
        return v.filter((x): x is string => typeof x === "string" && x.trim() !== "");
    }
    if (typeof v === "string") {
        const vv = v.trim();
        return vv === "" ? [] : [vv];
    }
    return [];
};

const TagsWithMore = (props: {
    labels: string[];
    variant: NonNullable<FoodsTagsProps["variant"]>;
    onClickAll?: (e: MouseEvent) => void;
}): JSX.Element => {
    const { labels, variant, onClickAll } = props;
    const [expanded, setExpanded] = useState(false);

    if (labels.length === 0) {
        return <></>;
    }

    const extra = Math.max(0, labels.length - 1);
    const shown = expanded ? labels : labels.slice(0, 1);

    return (
        <Stack
            direction="row"
            gap={1}
            sx={{ flexWrap: "wrap", alignItems: "center" }}
            onClick={(e) => {
                if (onClickAll) onClickAll(e);
            }}
        >
            {shown.map((label) => (
                <FoodsTags key={label} variant={variant} label={label} />
            ))}
            {!expanded && extra > 0 && (
                <Box
                    role="button"
                    onClick={(e: MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setExpanded(true);
                    }}
                >
                    <FoodsTags variant={variant} label={`+${extra}`} />
                </Box>
            )}
        </Stack>
    );
};

const parseRootProps = (page: PuckPage): Record<string, unknown> => {
    try {
        const obj: unknown = page.content ? JSON.parse(page.content) : {};
        if (!obj || typeof obj !== "object") return {};
        const root = (obj as { root?: unknown }).root;
        if (!root || typeof root !== "object") return {};
        const props = (root as { props?: unknown }).props;
        return props && typeof props === "object" ? (props as Record<string, unknown>) : {};
    } catch {
        return {};
    }
};

const upper = (value: string) => value.toUpperCase();

const buildTitleColumn = (args: BuildColumnsArgs): MRT_ColumnDef<PuckPage> => {
    const {
        t,
        isServerSide,
        editingTitle,
        isTitleUpdating,
        handleSaveTitle,
        setEditingTitle,
        getEditUrl,
        canModifyPage,
    } = args;

    return {
        accessorKey: "title",
        header: t("settings.cms.pages.table.title", "Title"),
        size: 300,
        enableSorting: true,
        enableColumnFilter: isServerSide,
        Filter: ({ column }) => (
            <FoodsTextInput
                value={(column.getFilterValue() as string) ?? ""}
                onChange={(value) => column.setFilterValue(value)}
            />
        ),
        Cell: ({ row }) => {
            if (canModifyPage && editingTitle?.slug === row.original.slug) {
                return (
                    <Stack
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            p: 1,
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <TextField
                            onClick={(e) => e.stopPropagation()}
                            value={editingTitle?.title}
                            onChange={(e) => setEditingTitle({ ...editingTitle!, title: e.target.value })}
                            disabled={isTitleUpdating}
                        />
                        {isTitleUpdating ?
                            <></>
                        :   <>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaveTitle();
                                    }}
                                >
                                    <CheckIcon />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingTitle(null);
                                    }}
                                >
                                    <CloseIcon color="error" />
                                </IconButton>
                            </>
                        }
                    </Stack>
                );
            }

            const url = getEditUrl ? getEditUrl(row.original.slug) : URLHandler.settings.cms.page(row.original.slug);
            return (
                <Stack
                    sx={{
                        display: "flex",
                        width: "100%",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                    }}
                >
                    {canModifyPage ?
                        <GridLink to={url} label={row.original.title} />
                    :   <GridText value={row.original.title} />}

                    {canModifyPage && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setEditingTitle({ slug: row.original.slug, title: row.original.title });
                            }}
                        >
                            <FoodsIcon icon="edit" size={2} />
                        </IconButton>
                    )}
                </Stack>
            );
        },
    };
};

const buildScoreColumn = (args: BuildColumnsArgs, isPdfTemplates: boolean): MRT_ColumnDef<PuckPage> | null => {
    const { t, scoreLabelById, handleOpenScoreCompanyDialog, canModifyPage } = args;
    if (!isPdfTemplates) return null;

    return {
        id: "score",
        header: t("settings.cms.templates.table.score", "Score"),
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
            const rootProps = parseRootProps(row.original);
            const scoreVals = normalizeStringList(rootProps.score);
            const scoreVariant = scoreVals.length === 0 ? "tags-green" : "tags-black";

            const scoreLabels =
                scoreVals.length === 0 ? [t("common.all", "All")] : scoreVals.map((id) => scoreLabelById?.[id] ?? id);

            return (
                <Box
                    role={canModifyPage ? "button" : undefined}
                    onClick={
                        canModifyPage ?
                            (e: MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleOpenScoreCompanyDialog?.(row.original);
                            }
                        :   undefined
                    }
                >
                    <TagsWithMore labels={scoreLabels} variant={scoreVariant} />
                </Box>
            );
        },
    };
};

const buildEntityColumn = (args: BuildColumnsArgs, isPdfTemplates: boolean): MRT_ColumnDef<PuckPage> | null => {
    const { t, companyLabelById, handleOpenScoreCompanyDialog, canModifyPage } = args;
    if (!isPdfTemplates) return null;

    return {
        id: "entity",
        header: t("settings.cms.templates.table.entity", "Entity"),
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
            const rootProps = parseRootProps(row.original);
            const entityVals = normalizeStringList(rootProps.entity);
            const entityVariant = entityVals.length === 0 ? "tags-green" : "tags-black";
            const entityLabels =
                entityVals.length === 0 ?
                    [t("common.all", "All")]
                :   entityVals.map((id) => companyLabelById?.[id] ?? id);

            return (
                <Box
                    role={canModifyPage ? "button" : undefined}
                    onClick={
                        canModifyPage ?
                            (e: MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleOpenScoreCompanyDialog?.(row.original);
                            }
                        :   undefined
                    }
                >
                    <TagsWithMore labels={entityLabels} variant={entityVariant} />
                </Box>
            );
        },
    };
};

const buildUrlColumn = (args: BuildColumnsArgs): MRT_ColumnDef<PuckPage> => {
    const { t, isServerSide, theme } = args;

    return {
        accessorKey: "url",
        header: t("settings.cms.pages.table.url", "URL"),
        enableSorting: false,
        enableColumnFilter: isServerSide,
        Filter: ({ column }) => (
            <FoodsTextInput
                value={(column.getFilterValue() as string) ?? ""}
                onChange={(value) => column.setFilterValue(value)}
            />
        ),
        Cell: ({ row }) => {
            const urlCopied = `${window.location.origin}${row.original.url}`;
            const relativeUrl = row.original.url;
            return (
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} width="100%">
                    {relativeUrl !== undefined && <GridLink to={relativeUrl} label={relativeUrl} inNewTab />}
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(urlCopied)}>
                        <ContentCopy sx={{ fontSize: theme.spacing(2.5) }} />
                    </IconButton>
                </Stack>
            );
        },
    };
};

const buildStatusColumn = (args: BuildColumnsArgs): MRT_ColumnDef<PuckPage> => {
    const { t, isServerSide, onUpdateStatus, canModifyPage } = args;

    return {
        accessorKey: "status",
        header: t("settings.cms.pages.table.active", "Active"),
        enableSorting: true,
        enableColumnFilter: isServerSide,
        Filter: ({ column }) => (
            <TextField
                select
                value={(column.getFilterValue() as string) ?? ""}
                onChange={(e) => column.setFilterValue(e.target.value)}
                size="small"
            >
                <MenuItem value="">{t("common.all", "All")}</MenuItem>
                <MenuItem value="active">{t("common.active", "Active")}</MenuItem>
                <MenuItem value="draft">{t("common.draft", "Draft")}</MenuItem>
            </TextField>
        ),
        Cell: ({ row }) => {
            const isActive = row.original.status === "active";
            const next = isActive ? "draft" : "active";
            return (
                <Box
                    role={canModifyPage ? "button" : undefined}
                    onClick={
                        canModifyPage ?
                            (e: MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onUpdateStatus?.(row.original.slug, next);
                            }
                        :   undefined
                    }
                >
                    <FoodsTags
                        variant={isActive ? "tags-green" : "tags-red"}
                        label={isActive ? t("common.active", "Active") : t("common.draft", "Draft")}
                    />
                </Box>
            );
        },
    };
};

const buildRolesColumn = (args: BuildColumnsArgs): MRT_ColumnDef<PuckPage> => {
    const { t, getPageRoles, getRoleDisplayName, handleOpenRolesDialog, theme, canModifyPage } = args;

    return {
        accessorKey: "roles",
        size: 300,
        header: t("settings.cms.pages.table.roles", "Roles"),
        enableSorting: false,
        enableColumnFilter: false,
        filterFn: (row, _, filterValue) => {
            if (!filterValue) return true;
            const pageRoles = getPageRoles(row.original);
            const searchTerm = (filterValue as string).toLowerCase();
            if (pageRoles.length === 0) {
                const publicLabel = t("settings.cms.pages.roles.public", "Public").toLowerCase();
                return publicLabel.includes(searchTerm);
            }
            return pageRoles.some(
                (role) =>
                    role.toLowerCase().includes(searchTerm) ||
                    getRoleDisplayName(role).toLowerCase().includes(searchTerm),
            );
        },
        Filter: ({ column }) => (
            <FoodsTextInput
                value={(column.getFilterValue() as string) ?? ""}
                onChange={(value) => column.setFilterValue(value)}
            />
        ),
        Cell: ({ row }) =>
            getPageRoles(row.original).length > 0 ?
                <Stack direction="row" gap={1} sx={{ flexWrap: "wrap", padding: theme.spacing(1, 0) }}>
                    {getPageRoles(row.original).map((role) => (
                        <Box
                            role={canModifyPage ? "button" : undefined}
                            key={role}
                            onClick={(e: MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (canModifyPage) handleOpenRolesDialog(row.original);
                            }}
                        >
                            <FoodsTags variant="tags-black" label={getRoleDisplayName(role)} />
                        </Box>
                    ))}
                </Stack>
            :   <Box
                    role={canModifyPage ? "button" : undefined}
                    onClick={(e: MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (canModifyPage) handleOpenRolesDialog(row.original);
                    }}
                >
                    <FoodsTags variant="tags-green" label={upper(t("settings.cms.pages.roles.public", "Public"))} />
                </Box>,
    };
};

const buildSectorsColumn = (args: BuildColumnsArgs): MRT_ColumnDef<PuckPage> => {
    const { t, isServerSide, allSectors, handleOpenSettingsDialog, theme, canModifyPage } = args;

    return {
        accessorKey: "sectors",
        header: t("settings.cms.pages.table.sector", "Sector"),
        enableSorting: false,
        minSize: 400,
        enableColumnFilter: isServerSide,
        Filter: ({ column }) => (
            <FoodsTextInput
                value={(column.getFilterValue() as string) ?? ""}
                onChange={(value) => column.setFilterValue(value)}
            />
        ),
        Cell: ({ row }) => {
            const iris = (row.original.sectors as string[] | undefined) || [];
            const names = iris
                .map((iri) => {
                    const match = iri.match(/\/(\d+)$/);
                    const id = match ? Number(match[1]) : null;
                    if (!id) return null;
                    const s = allSectors.find((sec) => sec.id === id);
                    return s ? s.name : null;
                })
                .filter(Boolean) as string[];

            return (
                <Box
                    role={canModifyPage ? "button" : undefined}
                    onClick={
                        canModifyPage ?
                            (e: MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleOpenSettingsDialog?.(row.original);
                            }
                        :   undefined
                    }
                >
                    {names.length === 0 ?
                        <FoodsTags variant={"tags-black"} label={upper(t("common.none", "None"))} />
                    :   <Stack direction="row" gap={1} sx={{ flexWrap: "wrap", padding: theme.spacing(1, 0) }}>
                            {names.map((name) => (
                                <FoodsTags
                                    key={name}
                                    sx={{
                                        textAlign: "left",
                                        cursor: canModifyPage ? "pointer" : "default",
                                        height: "fit-content",
                                    }}
                                    variant="tags-black"
                                    label={upper(name)}
                                />
                            ))}
                        </Stack>
                    }
                </Box>
            );
        },
    };
};

const buildDateColumn = (
    args: { t: TFunction; theme: Theme },
    key: "createdAt" | "updatedAt",
): MRT_ColumnDef<PuckPage> => {
    const { t } = args;
    const isCreated = key === "createdAt";

    return {
        accessorKey: key,
        header:
            isCreated ?
                t("settings.cms.pages.table.created_at", "Created")
            :   t("settings.cms.pages.table.updated_at", "Updated"),
        enableSorting: true,
        enableColumnFilter: false,
        filterFn: "datetime",
        Filter: ({ column }) => (
            <FoodsTextInput
                value={(column.getFilterValue() as string) ?? ""}
                onChange={(value) => column.setFilterValue(value)}
            />
        ),
        Cell: ({ row }) => (
            <GridText
                value={formatDateTime(
                    new Date(isCreated ? row.original.createdAt : row.original.updatedAt),
                    i18next.language,
                    { dateStyle: "short", timeStyle: "medium" },
                )}
            />
        ),
    };
};

export const buildPagesTableColumns = (args: BuildColumnsArgs): MRT_ColumnDef<PuckPage>[] => {
    const { pageType, theme, t } = args;

    const isPdfTemplates = pageType === "PDF";

    const titleColumn = buildTitleColumn(args);
    const urlColumn = buildUrlColumn(args);
    const statusColumn = buildStatusColumn(args);
    const rolesColumn = buildRolesColumn(args);
    const sectorsColumn = buildSectorsColumn(args);

    const scoreColumn = buildScoreColumn(args, isPdfTemplates);
    const entityColumn = buildEntityColumn(args, isPdfTemplates);

    const createdAtColumn = buildDateColumn({ t, theme }, "createdAt");
    const updatedAtColumn = buildDateColumn({ t, theme }, "updatedAt");

    const pdfColumns: MRT_ColumnDef<PuckPage>[] = [
        titleColumn,
        urlColumn,
        statusColumn,
        rolesColumn,
        sectorsColumn,
        ...(scoreColumn ? [scoreColumn] : []),
        ...(entityColumn ? [entityColumn] : []),
        createdAtColumn,
        updatedAtColumn,
    ];

    const cmsColumns: MRT_ColumnDef<PuckPage>[] = [
        titleColumn,
        urlColumn,
        statusColumn,
        rolesColumn,
        sectorsColumn,
        createdAtColumn,
        updatedAtColumn,
    ];

    return isPdfTemplates ? pdfColumns : cmsColumns;
};

export const buildCMSEditorColumns = buildPagesTableColumns;
